import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy, 
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Supported currencies with their symbols, flags, and locale settings
export const CURRENCIES = [
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳', locale: 'en-IN', decimals: 2 },
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸', locale: 'en-US', decimals: 2 },
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺', locale: 'de-DE', decimals: 2 },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧', locale: 'en-GB', decimals: 2 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵', locale: 'ja-JP', decimals: 0 },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺', locale: 'en-AU', decimals: 2 },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦', locale: 'en-CA', decimals: 2 },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭', locale: 'de-CH', decimals: 2 },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳', locale: 'zh-CN', decimals: 2 },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', flag: '🇭🇰', locale: 'zh-HK', decimals: 2 },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', flag: '🇳🇿', locale: 'en-NZ', decimals: 2 },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', flag: '🇸🇪', locale: 'sv-SE', decimals: 2 },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬', locale: 'en-SG', decimals: 2 },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', flag: '🇰🇷', locale: 'ko-KR', decimals: 0 },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', flag: '🇲🇽', locale: 'es-MX', decimals: 2 },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷', locale: 'pt-BR', decimals: 2 },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦', locale: 'en-ZA', decimals: 2 },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', flag: '🇷🇺', locale: 'ru-RU', decimals: 2 },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪', locale: 'ar-AE', decimals: 2 },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'SR', flag: '🇸🇦', locale: 'ar-SA', decimals: 2 },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', flag: '🇹🇭', locale: 'th-TH', decimals: 2 },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', flag: '🇲🇾', locale: 'ms-MY', decimals: 2 },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', flag: '🇮🇩', locale: 'id-ID', decimals: 0 },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', flag: '🇵🇭', locale: 'en-PH', decimals: 2 },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', flag: '🇵🇱', locale: 'pl-PL', decimals: 2 },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', flag: '🇹🇷', locale: 'tr-TR', decimals: 2 },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', flag: '🇳🇴', locale: 'nb-NO', decimals: 2 },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', flag: '🇩🇰', locale: 'da-DK', decimals: 2 },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪', flag: '🇮🇱', locale: 'he-IL', decimals: 2 },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨', flag: '🇵🇰', locale: 'ur-PK', decimals: 2 },
];

// Default fallback exchange rates (as of early 2026, relative to USD)
const FALLBACK_RATES = {
  USD: 1,
  INR: 83.50,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 149.50,
  AUD: 1.53,
  CAD: 1.35,
  CHF: 0.88,
  CNY: 7.20,
  HKD: 7.82,
  NZD: 1.62,
  SEK: 10.45,
  SGD: 1.34,
  KRW: 1320,
  MXN: 17.15,
  BRL: 4.95,
  ZAR: 18.50,
  RUB: 92.50,
  AED: 3.67,
  SAR: 3.75,
  THB: 35.20,
  MYR: 4.45,
  IDR: 15650,
  PHP: 55.80,
  PLN: 4.02,
  TRY: 30.50,
  NOK: 10.55,
  DKK: 6.88,
  ILS: 3.65,
  PKR: 280.50
};

// Exchange-rate APIs (free, no key required). Expected shape: { base?: string, rates: { [code]: number } }
const EXCHANGE_RATE_APIS = [
  process.env.REACT_APP_EXCHANGE_RATE_API_URL,
  'https://open.er-api.com/v6/latest/USD',
  'https://api.exchangerate-api.com/v4/latest/USD',
  'https://api.frankfurter.dev/v1/latest?base=USD'
].filter(Boolean);

const LOCAL_STORAGE_RATES_KEY = 'spend_easy_exchange_rates_v1';

const getLocalRatesCache = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_RATES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.rates && typeof parsed.rates === 'object' && Object.keys(parsed.rates).length > 0) {
      return parsed;
    }
  } catch {
    // Ignore storage access errors
  }
  return null;
};

const saveLocalRatesCache = (rates) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem(
      LOCAL_STORAGE_RATES_KEY,
      JSON.stringify({
        date: today,
        rates,
        timestamp: Date.now()
      })
    );
  } catch {
    // Ignore storage access errors
  }
};

// Extract rates from API response (support multiple shapes: rates, conversion_rates)
const parseRatesFromResponse = (data) => {
  if (!data) return null;
  const rates = data.rates || data.conversion_rates;
  if (rates && typeof rates === 'object' && Object.keys(rates).length > 0) {
    return rates;
  }
  return null;
};

const tryFetchRates = async (url, timeoutMs = 6000) => {
  try {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    const response = await fetch(url, {
      signal: controller ? controller.signal : undefined,
      headers: { Accept: 'application/json' }
    });
    if (timeoutId) clearTimeout(timeoutId);
    if (!response.ok) return null;
    const data = await response.json();
    return parseRatesFromResponse(data);
  } catch {
    // Gracefully handle network timeouts, CORS errors, or JSON errors
    return null;
  }
};

export const CurrencyProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [displayCurrency, setDisplayCurrency] = useState('INR');
  const [exchangeRates, setExchangeRates] = useState(FALLBACK_RATES);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [lastRatesUpdate, setLastRatesUpdate] = useState(null);
  const [ratesError, setRatesError] = useState(null);

  // Get currency info by code
  const getCurrencyInfo = useCallback((code) => {
    return CURRENCIES.find(c => c.code === code) || CURRENCIES[0];
  }, []);

  // Format amount with currency symbol and proper locale
  const formatCurrency = useCallback((amount, currencyCode) => {
    const code = currencyCode || displayCurrency;
    const currency = getCurrencyInfo(code);
    
    if (amount === undefined || amount === null || isNaN(amount)) {
      return `${currency.symbol}0`;
    }
    
    const absAmount = Math.abs(amount);
    const formattedAmount = absAmount.toLocaleString(currency.locale, { 
      minimumFractionDigits: currency.decimals, 
      maximumFractionDigits: currency.decimals 
    });
    
    const sign = amount < 0 ? '-' : '';
    return `${sign}${currency.symbol}${formattedAmount}`;
  }, [displayCurrency, getCurrencyInfo]);

  // Fetch exchange rates from API (with local and Firestore caching)
  const fetchExchangeRates = useCallback(async (forceRefresh = false) => {
    setRatesLoading(true);
    setRatesError(null);
    const today = new Date().toISOString().split('T')[0];

    try {
      // 1. If not a forced refresh, check cached rates first
      if (!forceRefresh) {
        const localCache = getLocalRatesCache();
        if (localCache?.date === today && localCache?.rates) {
          setExchangeRates(localCache.rates);
          setLastRatesUpdate(new Date(localCache.timestamp || Date.now()));
          setRatesLoading(false);
          return localCache.rates;
        }

        // User-scoped cache in Firestore (for authenticated non-guest users)
        if (currentUser && !currentUser.isGuest) {
          try {
            const cacheRef = doc(db, 'users', currentUser.uid, 'settings', 'exchangeRatesCache');
            const cacheSnap = await getDoc(cacheRef);
            if (cacheSnap.exists()) {
              const cachedData = cacheSnap.data();
              if (cachedData?.date === today && cachedData?.rates && Object.keys(cachedData.rates).length > 0) {
                setExchangeRates(cachedData.rates);
                setLastRatesUpdate(cachedData.timestamp?.toDate?.() || new Date());
                saveLocalRatesCache(cachedData.rates);
                setRatesLoading(false);
                return cachedData.rates;
              }
            }
          } catch {
            // Cache read failure: fall through to network fetch
          }
        }
      }

      // 2. Cascade through API endpoints
      let rawRates = null;
      for (const endpoint of EXCHANGE_RATE_APIS) {
        rawRates = await tryFetchRates(endpoint);
        if (rawRates && Object.keys(rawRates).length > 0) {
          break;
        }
      }

      if (!rawRates || Object.keys(rawRates).length === 0) {
        throw new Error('All exchange rate providers are unreachable');
      }

      // 3. Normalize to supported currencies, filling missing from FALLBACK_RATES
      const normalizedRates = { ...FALLBACK_RATES };
      Object.keys(FALLBACK_RATES).forEach((code) => {
        const val = rawRates[code];
        if (typeof val === 'number' && !isNaN(val) && val > 0) {
          normalizedRates[code] = val;
        }
      });

      // 4. Save to localStorage
      saveLocalRatesCache(normalizedRates);

      // 5. Save to Firestore if authenticated (not guest)
      if (currentUser && !currentUser.isGuest) {
        try {
          const cacheRef = doc(db, 'users', currentUser.uid, 'settings', 'exchangeRatesCache');
          await setDoc(cacheRef, {
            date: today,
            base: 'USD',
            rates: normalizedRates,
            timestamp: serverTimestamp()
          });
        } catch {
          // Cache write optional
        }
      }

      setExchangeRates(normalizedRates);
      setLastRatesUpdate(new Date());
      setRatesError(null);
      return normalizedRates;
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      const fallbackLocal = getLocalRatesCache();
      if (fallbackLocal?.rates) {
        setExchangeRates(fallbackLocal.rates);
        setLastRatesUpdate(new Date(fallbackLocal.timestamp || Date.now()));
        setRatesError(`Using cached rates (${error.message})`);
        if (forceRefresh) throw error;
        return fallbackLocal.rates;
      }

      setRatesError(error.message);
      setExchangeRates(FALLBACK_RATES);
      if (forceRefresh) throw error;
      return FALLBACK_RATES;
    } finally {
      setRatesLoading(false);
    }
  }, [currentUser]);

  // Convert amount between currencies
  // All rates are relative to USD, so:
  // To convert FROM currency A TO currency B:
  // 1. Convert A to USD: amountInUSD = amount / rates[A]
  // 2. Convert USD to B: result = amountInUSD * rates[B]
  const convertCurrency = useCallback((amount, fromCurrency, toCurrency, rates) => {
    const ratesObj = rates || exchangeRates;
    
    const numericAmount = typeof amount === 'number'
      ? amount
      : parseFloat(String(amount || '').replace(/,/g, '').trim());
    if (isNaN(numericAmount) || numericAmount === 0) return 0;
    if (fromCurrency === toCurrency) return numericAmount;
    if (!ratesObj || Object.keys(ratesObj).length === 0) return numericAmount;
    
    const fromRate = ratesObj[fromCurrency];
    const toRate = ratesObj[toCurrency];
    
    if (!fromRate || !toRate || isNaN(fromRate) || isNaN(toRate) || fromRate <= 0 || toRate <= 0) {
      console.warn(`Missing or invalid exchange rate for ${fromCurrency} (${fromRate}) or ${toCurrency} (${toRate})`);
      return numericAmount;
    }
    
    // Convert: amount in fromCurrency -> USD -> toCurrency
    const amountInUSD = numericAmount / fromRate;
    const result = amountInUSD * toRate;
    
    // Round to appropriate decimal places
    const toCurrencyInfo = getCurrencyInfo(toCurrency);
    const decimals = toCurrencyInfo?.decimals ?? 2;
    const multiplier = Math.pow(10, decimals);
    
    return Math.round(result * multiplier) / multiplier;
  }, [exchangeRates, getCurrencyInfo]);

  // Get current exchange rate between two currencies
  const getExchangeRate = useCallback((fromCurrency, toCurrency, rates) => {
    const ratesObj = rates || exchangeRates;
    
    if (fromCurrency === toCurrency) return 1;
    if (!ratesObj || Object.keys(ratesObj).length === 0) return 1;
    
    const fromRate = ratesObj[fromCurrency];
    const toRate = ratesObj[toCurrency];
    
    if (!fromRate || !toRate || isNaN(fromRate) || isNaN(toRate) || fromRate <= 0 || toRate <= 0) return 1;
    
    // Rate = how many units of toCurrency you get for 1 unit of fromCurrency
    return toRate / fromRate;
  }, [exchangeRates]);

  // Load user's currency preference from Firebase or localStorage
  useEffect(() => {
    const loadUserCurrency = async () => {
      // Always fetch exchange rates first
      await fetchExchangeRates();
      
      if (!currentUser) {
        setLoading(false);
        return;
      }

      if (currentUser.isGuest) {
        const demoCurrency = localStorage.getItem('spend_easy_demo_currency');
        if (demoCurrency) {
          setDisplayCurrency(demoCurrency);
        }
        try {
          const histRaw = localStorage.getItem('spend_easy_demo_conversion_history');
          if (histRaw) {
            setConversionHistory(JSON.parse(histRaw));
          }
        } catch (e) {}
        setLoading(false);
        return;
      }
      
      try {
        const userDocRef = doc(db, 'users', currentUser.uid, 'settings', 'currency');
        const docSnap = await getDoc(userDocRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setDisplayCurrency(data.displayCurrency || 'INR');
        }
        
        // Load conversion history
        try {
          const historyRef = collection(db, 'users', currentUser.uid, 'currency_conversions');
          const historyQuery = query(historyRef, orderBy('timestamp', 'desc'));
          const historySnap = await getDocs(historyQuery);
          
          const history = historySnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate() || new Date()
          }));
          setConversionHistory(history);
        } catch (historyError) {
          console.log('No conversion history found');
        }
        
      } catch (error) {
        console.error('Error loading currency settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserCurrency();
  }, [currentUser, fetchExchangeRates]);

  // Change display currency (without converting transactions)
  const changeDisplayCurrency = async (newCurrency) => {
    if (!currentUser) return;
    
    if (currentUser.isGuest) {
      setDisplayCurrency(newCurrency);
      localStorage.setItem('spend_easy_demo_currency', newCurrency);
      window.dispatchEvent(new Event('spend_easy_demo_data_updated'));
      return { success: true };
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid, 'settings', 'currency');
      await setDoc(userDocRef, { 
        displayCurrency: newCurrency,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      setDisplayCurrency(newCurrency);
      return { success: true };
    } catch (error) {
      console.error('Error changing currency:', error);
      throw error;
    }
  };

  // Convert all transactions to new currency (with undo support)
  const convertAllTransactions = async (newCurrency, transactionsToConvert) => {
    if (!currentUser) {
      return { success: false, error: 'No user logged in' };
    }

    // Ensure we have fresh rates
    let rates = null;
    try {
      rates = await fetchExchangeRates(true);
    } catch {
      rates = exchangeRates || FALLBACK_RATES;
    }
    if (!rates) rates = FALLBACK_RATES;

    const conversionRate = getExchangeRate(displayCurrency, newCurrency, rates);

    if (currentUser.isGuest) {
      // 1. Convert demo transactions
      const existingTxRaw = localStorage.getItem('spend_easy_demo_transactions');
      let txList = transactionsToConvert || [];
      if (existingTxRaw) {
        try {
          txList = JSON.parse(existingTxRaw);
        } catch (e) {
          txList = transactionsToConvert || [];
        }
      }

      const transactionSnapshots = [];
      const convertedTransactions = txList.map(t => {
        const origAmount = typeof t.amount === 'number'
          ? t.amount
          : parseFloat(String(t.amount || 0).replace(/,/g, '').trim()) || 0;
        const converted = convertCurrency(origAmount, displayCurrency, newCurrency, rates);
        transactionSnapshots.push({
          id: t.id,
          originalAmount: origAmount,
          originalCurrency: displayCurrency
        });
        return {
          ...t,
          amount: converted,
          originalAmount: t.originalAmount ?? origAmount,
          originalCurrency: t.originalCurrency ?? displayCurrency,
          displayCurrency: newCurrency,
          conversionRate: conversionRate,
          convertedAt: new Date().toISOString()
        };
      });

      localStorage.setItem('spend_easy_demo_transactions', JSON.stringify(convertedTransactions));

      // 2. Convert demo accounts
      const accountSnapshots = [];
      try {
        const accRaw = localStorage.getItem('spend_easy_demo_accounts');
        if (accRaw) {
          const accs = JSON.parse(accRaw);
          const convertedAccs = accs.map(acct => {
            if (typeof acct.initialBalance === 'number') {
              accountSnapshots.push({
                id: acct.id,
                originalInitialBalance: acct.initialBalance
              });
              return {
                ...acct,
                initialBalance: convertCurrency(acct.initialBalance, displayCurrency, newCurrency, rates)
              };
            }
            return acct;
          });
          localStorage.setItem('spend_easy_demo_accounts', JSON.stringify(convertedAccs));
        }
      } catch (e) {
        console.error('Failed to convert demo accounts:', e);
      }

      // 3. Convert demo category budgets
      let budgetSnapshots = null;
      try {
        const budgetRaw = localStorage.getItem('spend_easy_demo_budgets');
        if (budgetRaw) {
          const bData = JSON.parse(budgetRaw);
          budgetSnapshots = { ...bData };
          const convertedBudgets = {};
          Object.entries(bData).forEach(([cat, amount]) => {
            if (typeof amount === 'number') {
              convertedBudgets[cat] = convertCurrency(amount, displayCurrency, newCurrency, rates);
            }
          });
          localStorage.setItem('spend_easy_demo_budgets', JSON.stringify(convertedBudgets));
        }
      } catch (e) {
        console.error('Failed to convert demo budgets:', e);
      }

      // 4. Convert demo bills
      const billSnapshots = [];
      try {
        const billsRaw = localStorage.getItem('spend_easy_demo_bills');
        if (billsRaw) {
          const bills = JSON.parse(billsRaw);
          const convertedBills = bills.map(b => {
            if (typeof b.amount === 'number') {
              billSnapshots.push({ id: b.id, originalAmount: b.amount });
              return {
                ...b,
                amount: convertCurrency(b.amount, displayCurrency, newCurrency, rates)
              };
            }
            return b;
          });
          localStorage.setItem('spend_easy_demo_bills', JSON.stringify(convertedBills));
        }
      } catch (e) {
        console.error('Failed to convert demo bills:', e);
      }

      // 5. Convert demo subscriptions
      const subSnapshots = [];
      try {
        const subsRaw = localStorage.getItem('spend_easy_demo_subscriptions');
        if (subsRaw) {
          const subs = JSON.parse(subsRaw);
          const convertedSubs = subs.map(s => {
            if (typeof s.amount === 'number') {
              subSnapshots.push({ id: s.id, originalAmount: s.amount });
              return {
                ...s,
                amount: convertCurrency(s.amount, displayCurrency, newCurrency, rates)
              };
            }
            return s;
          });
          localStorage.setItem('spend_easy_demo_subscriptions', JSON.stringify(convertedSubs));
        }
      } catch (e) {
        console.error('Failed to convert demo subscriptions:', e);
      }

      // 6. Convert demo goals
      const goalSnapshots = [];
      try {
        const goalsRaw = localStorage.getItem('spend_easy_demo_goals');
        if (goalsRaw) {
          const goals = JSON.parse(goalsRaw);
          const convertedGoals = goals.map(g => {
            goalSnapshots.push({
              id: g.id,
              originalTarget: g.targetAmount,
              originalCurrent: g.currentAmount
            });
            return {
              ...g,
              targetAmount: typeof g.targetAmount === 'number'
                ? convertCurrency(g.targetAmount, displayCurrency, newCurrency, rates)
                : g.targetAmount,
              currentAmount: typeof g.currentAmount === 'number'
                ? convertCurrency(g.currentAmount, displayCurrency, newCurrency, rates)
                : g.currentAmount
            };
          });
          localStorage.setItem('spend_easy_demo_goals', JSON.stringify(convertedGoals));
        }
      } catch (e) {
        console.error('Failed to convert demo goals:', e);
      }

      // 7. Record conversion in history
      const conversionRecord = {
        id: `demo-conv-${Date.now()}`,
        fromCurrency: displayCurrency,
        toCurrency: newCurrency,
        conversionRate: conversionRate,
        timestamp: new Date(),
        transactionCount: convertedTransactions.length,
        canUndo: true,
        transactionSnapshots,
        accountSnapshots,
        budgetSnapshots,
        billSnapshots,
        subSnapshots,
        goalSnapshots,
        targetCurrency: newCurrency
      };

      try {
        const histRaw = localStorage.getItem('spend_easy_demo_conversion_history');
        const existingHist = histRaw ? JSON.parse(histRaw) : [];
        localStorage.setItem('spend_easy_demo_conversion_history', JSON.stringify([conversionRecord, ...existingHist]));
      } catch (e) {}

      setConversionHistory(prev => [conversionRecord, ...prev]);

      setDisplayCurrency(newCurrency);
      localStorage.setItem('spend_easy_demo_currency', newCurrency);

      window.dispatchEvent(new Event('spend_easy_demo_data_updated'));

      return {
        success: true,
        convertedCount: convertedTransactions.length,
        conversionId: conversionRecord.id,
        conversionRate: conversionRate
      };
    }

    try {
      const BATCH_SIZE = 400;
      let currentBatch = writeBatch(db);
      let opCount = 0;

      const commitAndResetBatch = async () => {
        if (opCount > 0) {
          await currentBatch.commit();
          currentBatch = writeBatch(db);
          opCount = 0;
        }
      };

      // Convert account initial balances
      let accountSnapshots = [];
      try {
        const accountsRef = collection(db, 'users', currentUser.uid, 'accounts');
        const accountsSnap = await getDocs(accountsRef);
        for (const docSnap of accountsSnap.docs) {
          const data = docSnap.data();
          if (typeof data.initialBalance === 'number') {
            const convertedInitial = convertCurrency(
              data.initialBalance,
              displayCurrency,
              newCurrency,
              rates
            );
            accountSnapshots.push({
              id: docSnap.id,
              originalInitialBalance: data.initialBalance
            });
            currentBatch.update(doc(db, 'users', currentUser.uid, 'accounts', docSnap.id), {
              initialBalance: convertedInitial
            });
            opCount++;
            if (opCount >= BATCH_SIZE) await commitAndResetBatch();
          }
        }
      } catch (acctErr) {
        console.error('Failed to convert account balances during currency conversion:', acctErr);
      }

      // Convert category budgets
      let budgetSnapshots = null;
      try {
        const budgetDocRef = doc(db, 'users', currentUser.uid, 'settings', 'budgets');
        const budgetSnap = await getDoc(budgetDocRef);
        if (budgetSnap.exists()) {
          const data = budgetSnap.data() || {};
          const categoryBudgets = data.categoryBudgets || {};
          budgetSnapshots = categoryBudgets;

          const newCategoryBudgets = {};
          Object.entries(categoryBudgets).forEach(([category, amount]) => {
            if (typeof amount === 'number') {
              newCategoryBudgets[category] = convertCurrency(
                amount,
                displayCurrency,
                newCurrency,
                rates
              );
            }
          });

          currentBatch.set(
            budgetDocRef,
            {
              categoryBudgets: newCategoryBudgets,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          opCount++;
          if (opCount >= BATCH_SIZE) await commitAndResetBatch();
        }
      } catch (budgetErr) {
        console.error('Failed to convert budgets during currency conversion:', budgetErr);
      }

      // Convert each transaction
      const transactionSnapshots = [];
      for (const transaction of (transactionsToConvert || [])) {
        const transRef = doc(db, 'users', currentUser.uid, 'transactions', transaction.id);
        const origAmount = typeof transaction.amount === 'number'
          ? transaction.amount
          : parseFloat(String(transaction.amount || 0).replace(/,/g, '').trim()) || 0;
        const convertedAmount = convertCurrency(origAmount, displayCurrency, newCurrency, rates);

        transactionSnapshots.push({
          id: transaction.id,
          originalAmount: origAmount,
          originalCurrency: displayCurrency
        });

        currentBatch.update(transRef, {
          amount: convertedAmount,
          originalAmount: transaction.amount,
          originalCurrency: displayCurrency,
          displayCurrency: newCurrency,
          conversionRate: conversionRate,
          convertedAt: serverTimestamp()
        });
        opCount++;
        if (opCount >= BATCH_SIZE) await commitAndResetBatch();
      }

      // Update user's display currency
      const currencyRef = doc(db, 'users', currentUser.uid, 'settings', 'currency');
      currentBatch.set(currencyRef, { 
        displayCurrency: newCurrency,
        updatedAt: serverTimestamp()
      }, { merge: true });
      opCount++;

      // Commit remaining batch
      await commitAndResetBatch();

      // Create conversion history record for undo
      const conversionRef = await addDoc(
        collection(db, 'users', currentUser.uid, 'currency_conversions'),
        {
          fromCurrency: displayCurrency,
          toCurrency: newCurrency,
          conversionRate: conversionRate,
          timestamp: serverTimestamp(),
          transactionCount: (transactionsToConvert || []).length,
          canUndo: true,
          transactionSnapshots,
          accountSnapshots,
          accountCount: accountSnapshots.length,
          budgetSnapshots,
          budgetCount: budgetSnapshots ? Object.keys(budgetSnapshots).length : 0,
          targetCurrency: newCurrency
        }
      );

      setDisplayCurrency(newCurrency);

      setConversionHistory(prev => [{
        id: conversionRef.id,
        fromCurrency: displayCurrency,
        toCurrency: newCurrency,
        conversionRate: conversionRate,
        timestamp: new Date(),
        transactionCount: (transactionsToConvert || []).length,
        canUndo: true
      }, ...prev]);

      return { 
        success: true, 
        convertedCount: (transactionsToConvert || []).length,
        conversionId: conversionRef.id,
        conversionRate: conversionRate
      };
    } catch (error) {
      console.error('Error converting transactions:', error);
      throw error;
    }
  };

  // Undo a currency conversion (within 7 days)
  const undoConversion = async (conversionId) => {
    if (!currentUser) return;

    if (currentUser.isGuest) {
      try {
        const historyRaw = localStorage.getItem('spend_easy_demo_conversion_history');
        const history = historyRaw ? JSON.parse(historyRaw) : conversionHistory;
        const conversionData = history.find(c => c.id === conversionId);
        if (!conversionData) {
          throw new Error('Conversion record not found');
        }

        const conversionDate = conversionData.timestamp instanceof Date
          ? conversionData.timestamp
          : new Date(conversionData.timestamp);
        const daysSince = Math.floor((Date.now() - conversionDate.getTime()) / (1000 * 60 * 60 * 24));
        if (daysSince > 7) {
          throw new Error('Cannot undo conversions older than 7 days');
        }

        // Restore transactions
        const txRaw = localStorage.getItem('spend_easy_demo_transactions');
        if (txRaw) {
          const txs = JSON.parse(txRaw);
          const snapMap = new Map((conversionData.transactionSnapshots || []).map(s => [s.id, s.originalAmount]));
          const restored = txs.map(t => {
            if (snapMap.has(t.id)) {
              return {
                ...t,
                amount: snapMap.get(t.id),
                originalAmount: null,
                originalCurrency: null,
                displayCurrency: null,
                conversionRate: null,
                convertedAt: null
              };
            }
            return t;
          });
          localStorage.setItem('spend_easy_demo_transactions', JSON.stringify(restored));
        }

        // Restore accounts
        if (Array.isArray(conversionData.accountSnapshots)) {
          const accRaw = localStorage.getItem('spend_easy_demo_accounts');
          if (accRaw) {
            const accs = JSON.parse(accRaw);
            const accMap = new Map(conversionData.accountSnapshots.map(s => [s.id, s.originalInitialBalance]));
            const restoredAccs = accs.map(a => {
              if (accMap.has(a.id)) {
                return { ...a, initialBalance: accMap.get(a.id) };
              }
              return a;
            });
            localStorage.setItem('spend_easy_demo_accounts', JSON.stringify(restoredAccs));
          }
        }

        // Restore budgets
        if (conversionData.budgetSnapshots) {
          localStorage.setItem('spend_easy_demo_budgets', JSON.stringify(conversionData.budgetSnapshots));
        }

        // Restore bills
        if (Array.isArray(conversionData.billSnapshots)) {
          const billsRaw = localStorage.getItem('spend_easy_demo_bills');
          if (billsRaw) {
            const bills = JSON.parse(billsRaw);
            const billMap = new Map(conversionData.billSnapshots.map(b => [b.id, b.originalAmount]));
            const restoredBills = bills.map(b => billMap.has(b.id) ? { ...b, amount: billMap.get(b.id) } : b);
            localStorage.setItem('spend_easy_demo_bills', JSON.stringify(restoredBills));
          }
        }

        // Restore subscriptions
        if (Array.isArray(conversionData.subSnapshots)) {
          const subsRaw = localStorage.getItem('spend_easy_demo_subscriptions');
          if (subsRaw) {
            const subs = JSON.parse(subsRaw);
            const subMap = new Map(conversionData.subSnapshots.map(s => [s.id, s.originalAmount]));
            const restoredSubs = subs.map(s => subMap.has(s.id) ? { ...s, amount: subMap.get(s.id) } : s);
            localStorage.setItem('spend_easy_demo_subscriptions', JSON.stringify(restoredSubs));
          }
        }

        // Restore goals
        if (Array.isArray(conversionData.goalSnapshots)) {
          const goalsRaw = localStorage.getItem('spend_easy_demo_goals');
          if (goalsRaw) {
            const goals = JSON.parse(goalsRaw);
            const goalMap = new Map(conversionData.goalSnapshots.map(g => [g.id, g]));
            const restoredGoals = goals.map(g => {
              if (goalMap.has(g.id)) {
                const snap = goalMap.get(g.id);
                return { ...g, targetAmount: snap.originalTarget, currentAmount: snap.originalCurrent };
              }
              return g;
            });
            localStorage.setItem('spend_easy_demo_goals', JSON.stringify(restoredGoals));
          }
        }

        // Restore display currency
        setDisplayCurrency(conversionData.fromCurrency);
        localStorage.setItem('spend_easy_demo_currency', conversionData.fromCurrency);

        const updatedHistory = history.filter(c => c.id !== conversionId);
        localStorage.setItem('spend_easy_demo_conversion_history', JSON.stringify(updatedHistory));
        setConversionHistory(updatedHistory);

        window.dispatchEvent(new Event('spend_easy_demo_data_updated'));

        return {
          success: true,
          restoredCurrency: conversionData.fromCurrency
        };
      } catch (error) {
        console.error('Error undoing demo conversion:', error);
        throw error;
      }
    }

    // Authenticated Firestore undo
    try {
      const conversionRef = doc(db, 'users', currentUser.uid, 'currency_conversions', conversionId);
      const conversionSnap = await getDoc(conversionRef);
      if (!conversionSnap.exists()) {
        throw new Error('Conversion not found');
      }

      const conversionData = conversionSnap.data();
      const conversionDate = conversionData.timestamp?.toDate() || new Date();
      const daysSinceConversion = Math.floor((Date.now() - conversionDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceConversion > 7) {
        throw new Error('Cannot undo conversions older than 7 days');
      }
      if (!conversionData.canUndo) {
        throw new Error('This conversion cannot be undone');
      }

      const BATCH_SIZE = 400;
      let currentBatch = writeBatch(db);
      let opCount = 0;

      const commitAndResetBatch = async () => {
        if (opCount > 0) {
          await currentBatch.commit();
          currentBatch = writeBatch(db);
          opCount = 0;
        }
      };

      for (const snapshot of (conversionData.transactionSnapshots || [])) {
        const transRef = doc(db, 'users', currentUser.uid, 'transactions', snapshot.id);
        currentBatch.update(transRef, {
          amount: snapshot.originalAmount,
          originalAmount: null,
          originalCurrency: null,
          displayCurrency: null,
          conversionRate: null,
          convertedAt: null
        });
        opCount++;
        if (opCount >= BATCH_SIZE) await commitAndResetBatch();
      }

      if (Array.isArray(conversionData.accountSnapshots)) {
        for (const acct of conversionData.accountSnapshots) {
          if (!acct.id || typeof acct.originalInitialBalance !== 'number') continue;
          const acctRef = doc(db, 'users', currentUser.uid, 'accounts', acct.id);
          currentBatch.update(acctRef, {
            initialBalance: acct.originalInitialBalance
          });
          opCount++;
          if (opCount >= BATCH_SIZE) await commitAndResetBatch();
        }
      }

      if (conversionData.budgetSnapshots) {
        const budgetDocRef = doc(db, 'users', currentUser.uid, 'settings', 'budgets');
        currentBatch.set(budgetDocRef, {
          categoryBudgets: conversionData.budgetSnapshots,
          updatedAt: serverTimestamp()
        }, { merge: true });
        opCount++;
        if (opCount >= BATCH_SIZE) await commitAndResetBatch();
      }

      const currencyRef = doc(db, 'users', currentUser.uid, 'settings', 'currency');
      currentBatch.set(currencyRef, { 
        displayCurrency: conversionData.fromCurrency,
        updatedAt: serverTimestamp()
      }, { merge: true });
      opCount++;

      currentBatch.delete(conversionRef);
      opCount++;

      await commitAndResetBatch();

      setDisplayCurrency(conversionData.fromCurrency);
      setConversionHistory(prev => prev.filter(c => c.id !== conversionId));

      return { 
        success: true, 
        restoredCurrency: conversionData.fromCurrency 
      };
    } catch (error) {
      console.error('Error undoing conversion:', error);
      throw error;
    }
  };

  // Check if a conversion can be undone
  const canUndoConversion = useCallback((conversion) => {
    if (!conversion.canUndo) return false;
    
    const conversionDate = conversion.timestamp instanceof Date 
      ? conversion.timestamp 
      : conversion.timestamp?.toDate?.() || new Date();
    const daysSinceConversion = Math.floor((Date.now() - conversionDate.getTime()) / (1000 * 60 * 60 * 24));
    
    return daysSinceConversion <= 7;
  }, []);

  // Refresh exchange rates manually (forces live fetch from API)
  const refreshRates = useCallback(async () => {
    return await fetchExchangeRates(true);
  }, [fetchExchangeRates]);

  const currencyInfo = getCurrencyInfo(displayCurrency) || { code: displayCurrency, symbol: '$' };
  const currencySymbol = currencyInfo.symbol || '$';

  const value = {
    currency: currencyInfo,
    currencySymbol,
    displayCurrency,
    exchangeRates,
    conversionHistory,
    loading,
    ratesLoading,
    ratesError,
    lastRatesUpdate,
    CURRENCIES,
    getCurrencyInfo,
    formatCurrency,
    fetchExchangeRates,
    refreshRates,
    convertCurrency,
    getExchangeRate,
    changeDisplayCurrency,
    convertAllTransactions,
    undoConversion,
    canUndoConversion
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
